import {
    AdvancedText,
    BackgroundConstants,
    Button,
    CoreDI,
    DI,
    Easing,
    IAdvancedTextConfig,
    GameConfig,
    inject,
    LanguageService,
    LAYERS,
    ObjectBuilder,
    ReelsScaleService,
    Spine,
    State,
    SymbolPosition,
    Timer,
    Tween,
    UpdateManager,
    View
} from '@gongxigames/framework/slot';
import {Layer} from '@pixi/layers';
import {Container, DisplayObject, Sprite, Text, Texture} from 'pixi.js';
import {IAnimationState} from 'pixi-spine';
import {PickBonusConstants} from 'src/pickBonus/PickBonusConstants';
import {PickData} from 'src/pickBonus/PickBonusModule';
import {LocalGameConfig} from 'src/configs/GameConfig';
import {WolfHaven} from 'com/gongxigames/math/wolfhaven/engine/WolfHaven';
import {defaultStyleForPick} from 'src/configs/Styles';

export interface CoinData {
    animation: Spine | null;
    sheen: Spine | null;
    coinAnimationContainer: Sprite;
    fromPos: SymbolPosition;
    flightToPositions?: SymbolPosition;
}

export class PickBonusView extends View {
    public static readonly NAME: symbol = Symbol('PickBonusView');
    public static readonly COIN_CLICKED: symbol = Symbol('COIN_CLICKED');
    public static readonly PICK_WIN_FINISHED: symbol = Symbol('PICK_WIN_FINISHED');
    public static readonly UPDATE_PICKED: symbol = Symbol('UPDATE_PICKED');
    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;
    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;
    protected _baseContainer: Container = new Container();
    protected _updater: UpdateManager = new UpdateManager(true);
    protected _placement?: Spine;
    protected _shadows?: Spine;
    protected _coins: (Sprite | null)[] = [];
    protected _coinsData: (CoinData | null)[] = [];
    protected _revealContainer: Sprite = new Sprite();
    protected _bonusContainer!: Sprite;
    protected _autoPickButton!: Button;
    protected _fgSegments: Sprite[] = [];
    protected _diamonds: Sprite[] = [];
    protected _sheenTimer!: Timer;
    protected _isAutoPick = false;
    protected _isFinished = false;
    protected _advancedTexts: IAdvancedTextConfig[] = [];
    protected _eventsQue: number[] = [];

    public init(): void {
        if ((this._gameConfig as LocalGameConfig).pickBonusTXT) {
            this._advancedTexts = (this._gameConfig as LocalGameConfig).pickBonusTXT!;
        }
        super.init();
        this.container.addChild(this._revealContainer);
        this.updateManager.add(this._updater);
    }

    public startRevealAnimation(): void {
        const reveal = this.createAnimation('pick_game_bonus_reveal', 'reveal', () => {
            this.enableAllCoins();
            this._autoPickButton.enable();
        });
        const slotName = 'revealedimage[mask_alpha]';
        const state = reveal.state as IAnimationState;

        reveal.x = this._gameConfig.mainFrame.width / 2;
        reveal.y = this._gameConfig.mainFrame.height / 2;
        this._revealContainer.addChild(reveal);
        const slot = reveal.skeleton.findSlot(slotName);
        const {currentSprite} = slot;

        state.addListener({
            event: (entry, event) => {
                if (event.data.name === 'kek') {
                    this.container.addChild(this._baseContainer);
                }
            }
        });
        currentSprite.addChild(this._baseContainer);
        currentSprite.x = -this._gameConfig.mainFrame.width / 2;
        currentSprite.y = this._gameConfig.mainFrame.height / 2;
        currentSprite.scale.set(1, -1);
        reveal.start();
    }

    public start(): void {
        this.initControls();
        this.initShadows();
        this.initCoinPositions();
        this.resetDiamonds();
        this.initTexts();
    }

    public clearView(): void {
        this._updater.removeAll();
        this._baseContainer.removeChildren();
        this._baseContainer.alpha = 1;
        this._placement = undefined;
        this._shadows = undefined;
        this._coins = [];
        this._coinsData = [];
        this._fgSegments = [];
        this._diamonds = [];
        this._isAutoPick = false;
        this._isFinished = false;
        this._eventsQue = [];
    }

    protected resetDiamonds(): void {
        this._diamonds.forEach(diamonds => {
            (diamonds as Sprite).children.forEach(diamond => (diamond as Sprite).children[0].visible = false);
        });
    }

    protected initControls(): void {
        this._bonusContainer = this._objectBuilder.buildSprite(ObjectBuilder.resources.game_sprite.bonus);
        this._baseContainer.addChild(this._bonusContainer);
        this._autoPickButton = this._bonusContainer.getChildByName('autoPick') as Button;
        this._autoPickButton.callback = this.onAutoPickClicked.bind(this);
        this._autoPickButton.disable();
        const fgSegment0 = this._bonusContainer.getChildByName('fg_segment0') as Sprite;

        this._fgSegments.push(fgSegment0);
        this._diamonds.push(fgSegment0.getChildByName('diamonds') as Sprite);
        const fgSegment1 = this._bonusContainer.getChildByName('fg_segment1') as Sprite;

        this._fgSegments.push(fgSegment1);
        this._diamonds.push(fgSegment1.getChildByName('diamonds') as Sprite);
        const fgSegment2 = this._bonusContainer.getChildByName('fg_segment2') as Sprite;

        this._fgSegments.push(fgSegment2);
        this._diamonds.push(fgSegment2.getChildByName('diamonds') as Sprite);
        this._baseContainer.addChild(this._autoPickButton);
    }

    protected onAutoPickClicked(): void {
        this.playButtonClickSound();
        this._isAutoPick = true;
        this._autoPickButton.disable();
        this.disableAllCoins();

        if (!this.isWaitingForAnimationToBeFinished() && !this._eventsQue.length) {
            this._updater.addTimer(() => {
                this.startNextAutoPick();
            }, PickBonusConstants.AUTO_PLAY_DELAY_TIME, true);
        }
    }

    protected isWaitingForAnimationToBeFinished(): boolean {
        return this._coinsData.some(coinData => coinData && coinData.animation && coinData.animation.isPlaying);
    }

    protected startNextAutoPick(): void {
        const activeCoins = this._coins.filter(coin => coin);
        const randomCoin = activeCoins[Math.floor(Math.random()
            * (activeCoins.length))]!;

        randomCoin.emit('click');
    }

    protected initTexts(): void {
        const {FREE_SPINS_TEXT_ID, REMOVED_TEXT_ID, MEDALLION_TEXT_ID, AUTO_PICK_TEXT_ID} = PickBonusConstants;

        this._advancedTexts.forEach(data => {
            if (data.id === FREE_SPINS_TEXT_ID) {
                this._fgSegments.forEach(segment => {
                    const txt = segment.getChildByName('FGText') as Text;

                    if (txt) {
                        this.translateText(txt, segment, data);
                    }
                });
            } else if (data.id === REMOVED_TEXT_ID) {
                this._fgSegments.forEach(segment => {
                    const txt = segment.getChildByName('RemovedText') as Text;

                    if (txt) {
                        this.translateText(txt, segment, data);
                    }
                });
            } else if (data.id === MEDALLION_TEXT_ID) {
                const txt = this._bonusContainer.getChildByName('pickText') as Text;

                if (txt) {
                    this.translateText(txt, this._bonusContainer, data);
                }
            } else if (data.id === AUTO_PICK_TEXT_ID) {
                for (const frame of Object.keys(this._autoPickButton.frames)) {
                    const childSprite = this._autoPickButton.frames[frame as State] as Sprite;
                    const text = childSprite.getChildByName('AutoPickText');
                    const translatedText = new AdvancedText(this.objectBuilder, this._languageService.get(data.id), data.style, this._screenManager.defaultViewportResolution);
                    const buttonBack = childSprite.getChildByName('btn_back') as Sprite;

                    if (data.maxWidth) {
                        translatedText.maxWidth = data.maxWidth;
                    }
                    translatedText.x = buttonBack.x + buttonBack.width / 2 + 10;
                    translatedText.y = buttonBack.y + buttonBack.height / 2 - translatedText.height / 2;

                    // To force text that separated into two lines be closer to each other and aligned
                    if (translatedText.children.length > 1) {
                        translatedText.children[0].y += translatedText.children[1].y / 5;
                        translatedText.children[1].y -= translatedText.children[1].y / 5;
                    }
                    text && childSprite.removeChild(text);
                    childSprite.addChild(translatedText);
                }
            }
        });
        this._fgSegments.forEach(segment => {
            const txt = segment.getChildByName('FGAmount') as Text;

            if (txt) {
                txt.text = String(WolfHaven.FREE_GAMES_COUNT);
                txt.style = defaultStyleForPick as any;
                txt.style.fontSize = 140;
            }
        });
    }

    protected translateText(txt: Text, sprite: Sprite, data: IAdvancedTextConfig): void {
        const translatedText = new AdvancedText(this.objectBuilder, this._languageService.get(data.id), data.style, this._screenManager.defaultViewportResolution);

        translatedText.x = txt.x;
        translatedText.y = txt.y + txt.height / 2;

        if (data.maxWidth) {
            translatedText.maxWidth = data.maxWidth;
        }
        sprite.removeChild(txt);
        sprite.addChild(translatedText);
    }

    protected initShadows(): void {
        this._shadows = this.objectBuilder.buildSpine('pick_game_shadows');
        this._shadows.x = this._gameConfig.mainFrame.width / 2;
        this._shadows.y = this._gameConfig.mainFrame.height / 2;
        this._shadows.update(0);
        this._shadows.start();
        this._updater.add(this._shadows);
        this._baseContainer.addChild(this._shadows);
    }

    protected initCoinPositions(): void {
        this._placement = this.objectBuilder.buildSpine('pick_game_coinplacement');
        this._placement.x = this._gameConfig.mainFrame.width / 2;
        this._placement.y = this._gameConfig.mainFrame.height / 2;
        this._baseContainer.addChild(this._placement);
    }

    public createCoins(itemsAmount: number): void {
        const skeleton = this._placement!.skeleton;

        this.clearSlot(this._placement!, 'fg_1');
        this.clearSlot(this._placement!, 'fg_2');
        this.clearSlot(this._placement!, 'fg_3');
        for (let i = 0; i < itemsAmount; i++) {
            const slotName = 'coin' + (i + 1);
            const sheen = this.createAnimation('pick_game_coin_sheen', 'coin_sheen');
            const coin = sheen.skeleton.findSlot('jp_coin').currentSprite;
            const coinIndex = i;

            sheen.update(0);
            sheen.start();
            coin.interactive = true;
            coin.buttonMode = true;
            coin.on('click', this.onCoinClicked.bind(this, coinIndex, sheen));
            coin.on('tap', this.onCoinClicked.bind(this, coinIndex, sheen));
            coin.onClick = () => this.onCoinClicked.bind(this, coinIndex, sheen);
            coin.hitArea = this._objectBuilder.buildGeom({
                type: 'circle',
                x: 0,
                y: 0,
                radius: coin.width / 2
            });
            this._coins[coinIndex] = coin;
            const coinAnimationContainer = new Sprite();
            const from = {
                x: skeleton.findSlot(slotName).bone.worldX + this._placement!.x,
                y: skeleton.findSlot(slotName).bone.worldY + this._placement!.y + PickBonusConstants.COIN_PLACEMENT_Y_OFFSET
            };

            coinAnimationContainer.addChild(sheen);
            this.addToSlot(this._placement!, coinAnimationContainer, slotName);
            const coinData: CoinData = {
                animation: null,
                sheen,
                coinAnimationContainer,
                fromPos: from
            };

            this._coinsData.push(coinData);
        }
        this.disableAllCoins();
        this.startSheenTimer();
    }

    protected startSheenTimer(): void {
        this.stopSheenTimer();
        this._sheenTimer = new Timer(() => {
            this._coinsData.forEach((coin) => {
                if (coin && coin.sheen) {
                    coin.sheen.state.setAnimation(0, 'coin_sheen', false);
                    coin.sheen.start();
                }
            });
            this.startSheenTimer();
        }, PickBonusConstants.SHEEN_TIMER, true);
        this._updater.add(this._sheenTimer);
    }

    protected stopSheenTimer(): void {
        if (this._sheenTimer && this._sheenTimer.isPlaying) {
            this._sheenTimer.stop();
            this._updater.remove(this._sheenTimer);
        }
    }

    protected playButtonClickSound(): void {
        this.soundPlayer.play('buttonclick');
    }

    protected playClickSound(): void {
        this.soundPlayer.play('coinclick');
    }

    protected playFlightSound(): void {
        this.soundPlayer.play('diamondflight');
    }

    protected playGemFillSound(): void {
        this.soundPlayer.play('gemfill');
    }

    protected playPickAwardSound(): void {
        this.soundPlayer.play('pickAward');
    }

    protected onCoinClicked(coinIndex: number): void {
        this.playClickSound();

        if (!this._eventsQue.length) {
            this.emit(PickBonusView.COIN_CLICKED, coinIndex);
        }
        this._eventsQue.push(coinIndex);
        this._coins[coinIndex]!.interactive = false;
        this._coins[coinIndex]!.buttonMode = false;
        this._coins[coinIndex] = null;
    }

    public pick(index: number, value: number, pickData?: PickData): void {
        this._isFinished = !!(pickData && this.isFinished(pickData));
        this._eventsQue.splice(0, 1);

        if (this._isFinished) {
            this.disableAllCoins();
            this._autoPickButton.disable();
            this._isAutoPick = false;
        }

        if (!this._isFinished && this._eventsQue.length > 0) {
            this.emit(PickBonusView.COIN_CLICKED, this._eventsQue[0]);
        }
        this._shadows!.state.setAnimation(index, `shadow_${index + 1}`, false);
        const coinFlip = this.createAnimation('pick_game_coinflip', 'flip');
        const state = coinFlip.state as IAnimationState;

        this._coinsData[index]!.coinAnimationContainer.removeChild(this._coinsData[index]!.sheen!);
        this._coinsData[index]!.animation = coinFlip;
        this._coinsData[index]!.sheen = null;
        this._coinsData[index]!.coinAnimationContainer.addChild(coinFlip);
        coinFlip.update(0);
        coinFlip.start();
        state.addListener({
            complete: () => {
                this._coinsData[index]!.coinAnimationContainer.removeChild(coinFlip);
                coinFlip.stop();
            },
            event: (entry, event) => {
                if (event.data.name === 'diamond') {
                    const diamond = this.createAnimation('pick_game_diamonds', String(value), () => {
                        this.onDiamondAnimationFinished(index, pickData!);
                    });

                    diamond.scale.set(1, -1);
                    this._coinsData[index]!.animation = diamond;
                    this._coinsData[index]!.coinAnimationContainer.addChild(diamond);
                    this.prepareFlight(diamond, this._coinsData[index]!, pickData!);
                    diamond.start();
                }
            }
        });
        this._coinsData[index]!.animation!.start();
        this.emit(PickBonusView.UPDATE_PICKED, index, value);
    }

    protected isFinished(pickData: PickData): boolean {
        let isFinished = false;

        if (pickData.type > 0 && pickData.indexes.length === 3) {
            isFinished = true;
        } else if (pickData.type === 0 && pickData.indexes.length === 4) {
            isFinished = true;
        }

        return isFinished;
    }

    protected onDiamondAnimationFinished(index: number, pickData: PickData): void {
        this._coinsData[index]!.animation!.stop();

        if (pickData && this.isFinished(pickData)) {
            this._coins.forEach(coin => coin && coin.removeAllListeners());
        } else if (this._isAutoPick && !this.isWaitingForAnimationToBeFinished() && !this._eventsQue.length) {
            this._updater.addTimer(() => {
                this.startNextAutoPick();
            }, PickBonusConstants.AUTO_PLAY_DELAY_TIME, true);
        }
    }

    protected prepareFlight(coin: Spine, coinData: CoinData, pickData: PickData): void {
        this.getFinalPosition(coinData, pickData.type);
        const moveContainer = new Container();
        const state = coin.state as IAnimationState;

        moveContainer.x = coinData.fromPos.x;
        moveContainer.y = coinData.fromPos.y;
        coinData.coinAnimationContainer.x = 0;
        coinData.coinAnimationContainer.y = 0;
        moveContainer.addChild(coinData.coinAnimationContainer);
        this._baseContainer.addChild(moveContainer);
        state.addListener({
            event: (entry, event) => {
                if (event.data.name === 'flight_start') {
                    this.playFlightSound();
                    const tween = new Tween(moveContainer)
                        .easing(Easing.Sinusoidal.InOut)
                        .to({
                            x: coinData.flightToPositions!.x,
                            y: coinData.flightToPositions!.y
                        }, PickBonusConstants.FLYING_TIME)
                        .start();

                    this._updater.add(tween);
                } else if (event.data.name === 'flight_stop') {
                    const segmentAnimationContainer = this._fgSegments[pickData.type]
                        .getChildByName('animationContainer') as Container;

                    segmentAnimationContainer.removeChildren();
                    const hitAnimation = this.createAnimation('pick_game_segment', 'hit', () => {
                        segmentAnimationContainer.removeChildren();

                        if (pickData && this.isFinished(pickData)) {
                            this.playPickAwardSound();
                            this.playWin(pickData);
                        }
                    });

                    segmentAnimationContainer.addChild(hitAnimation);
                    hitAnimation.start();
                    this.playGemFillSound();
                    this.showDiamond(pickData.type);
                }
            }
        });
    }

    public killCoin(coinIndex: number): void {
        this._shadows!.state.setAnimation(coinIndex, `shadow_${coinIndex + 1}`, false);
        this._coins[coinIndex] = null;
        this._coinsData[coinIndex]!.sheen!.stop();
        this._coinsData[coinIndex]!.sheen = null;
        this._coinsData[coinIndex]!.coinAnimationContainer.removeChildren();
        this._coinsData[coinIndex] = null;
    }

    protected getFinalPosition(coinData: CoinData, fgSegmentIndex: number): void {
        const currentDiamonds = this._diamonds[fgSegmentIndex].children;

        for (const current of currentDiamonds) {
            const currentDiamond = (current as Sprite).children[0];
            const animationContainer = (current as Sprite).children[1] as Sprite;

            if (currentDiamond.visible === false) {
                coinData.flightToPositions = {
                    x: animationContainer.x,
                    y: animationContainer.y - PickBonusConstants.DIAMOND_SYMBOL_SLOT.height / 2
                };
                break;
            }
        }
    }

    public showDiamond(fgSegmentIndex: number): void {
        const currentDiamonds = this._diamonds[fgSegmentIndex].children;

        for (const current of currentDiamonds) {
            const currentDiamond = (current as Sprite).children[0];
            const animationContainer = (current as Sprite).children[1] as Sprite;

            if (currentDiamond.visible === false) {
                const sparkleAnimation = this.createAnimation('pick_game_segment', 'sparkle', () => {
                    animationContainer.removeChildren();
                });

                animationContainer.addChild(sparkleAnimation);
                sparkleAnimation.x += PickBonusConstants.DIAMOND_SYMBOL_SLOT.width / 2;
                sparkleAnimation.y += PickBonusConstants.DIAMOND_SYMBOL_SLOT.height / 2;
                sparkleAnimation.start();
                currentDiamond.visible = true;

                return;
            }
        }
    }

    protected fadeAwayPickView(): void {
        this._updater.add(new Tween(this._baseContainer)
            .to({alpha: 0.0}, BackgroundConstants.TRANSFER_TIME)
            .onComplete(() => {
                this.clearView();
            })
            .start());
    }

    protected playWin(pickData: PickData): void {
        const animationContainer = this._fgSegments[pickData.type].getChildByName('animationContainer') as Container;
        const winAnimation = this.createAnimation('pick_game_segment', 'win', undefined, true);

        animationContainer.addChild(winAnimation);
        winAnimation.start();
        this._updater.addTimer(() => {
            animationContainer.children.forEach(child => (child as Spine).stop());
            animationContainer.removeChildren();
            this.fadeAwayPickView();
            this.emit(PickBonusView.PICK_WIN_FINISHED, pickData.type);
        }, PickBonusConstants.FREE_SPINS_OUTRO_START_DELAY, true);
    }

    protected createAnimation(spineName: string, animationName: string, completeCallBack?: Function, loop = false): Spine {
        const spine = this.objectBuilder.buildSpine(spineName);
        const state = spine.state as IAnimationState;

        spine.state.setAnimation(0, animationName, loop);
        state.addListener({
            complete: () => completeCallBack && completeCallBack()
        });
        spine.update(0);
        this.spineUtils.applyMasks(spine);
        this._updater.add(spine);

        return spine;
    }

    protected disableAllCoins(): void {
        this._coins.forEach(coin => {
            if (coin) {
                coin.interactive = false;
                coin.buttonMode = false;
            }
        });
    }

    protected enableAllCoins(): void {
        this._coins.forEach(coin => {
            if (coin) {
                coin.interactive = true;
                coin.buttonMode = true;
            }
        });
    }

    protected addToSlot(spine: Spine, displayObject: DisplayObject, slotName: string): DisplayObject {
        const slot = spine.skeleton.findSlot(slotName);
        const {currentSprite} = slot;
        const invertedScaleX = Math.abs(1 / currentSprite.scale.x);
        const invertedScaleY = Math.abs(1 / currentSprite.scale.y);

        this.clearSlot(spine, slotName);
        displayObject.scale.set(invertedScaleX * displayObject.scale.x, invertedScaleY * -displayObject.scale.y);
        currentSprite.addChild(displayObject);
        displayObject.x = slot.bone.ax;
        displayObject.y = slot.bone.ay;

        return displayObject;
    }

    protected clearSlot(spine: Spine, slotName: string): void {
        const slot = spine.skeleton.findSlot(slotName);
        const {currentSprite} = slot;

        currentSprite.removeChildren();
        currentSprite.texture = Texture.EMPTY;
    }

    public onLandscape(): void {
        super.onLandscape();
        this._reelsScaleService.scaleByCurrentScreenOrientation(this.container);
        // to not cover balance on landscape and to alight position after scaling
        this.container.scale.set(0.98);
        this.container.x = 50;
    }

    public onPortrait(): void {
        super.onPortrait();
        this._reelsScaleService.scaleByCurrentScreenOrientation(this.container);
    }

    public static layer: Layer = LAYERS.OVER_KEYPAD;
    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: ReelsScaleService;
}
